import React from 'react';

import PropTypes from 'prop-types';

import { omitKeys } from '@adsk/alloy-react-helpers';

import {
  NUMERIC_INPUT_TYPES,
  SIGN_DISPLAY_TYPES,
  TRAILING_ZERO_DISPLAY_TYPES,
} from '../consts';
import { useNumericInput } from '../hooks';
import TextInput from '../TextInput';
import {
  NumericInputType,
  SignDisplayType,
  TrailingZeroDisplayType,
  NumericInputChangeEvent,
} from '../types';

type NumericInputProps = Omit<
  React.ComponentProps<typeof TextInput>,
  | 'defaultValue'
  | 'renderIcon'
  | 'renderLeftIcon'
  | 'type'
  | 'value'
  | 'onChange'
> & {
  type?: NumericInputType;
  defaultValue?: number | null;
  value?: number | null;
  allowNegative?: boolean;
  locale?: string | string[];
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  maximumSignificantDigits?: number;
  trailingZeroDisplay?: TrailingZeroDisplayType;
  signDisplay?: SignDisplayType;
  useGrouping?: boolean;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (event: NumericInputChangeEvent) => void;
};

const NumericInput = React.forwardRef<HTMLInputElement, NumericInputProps>(
  (
    {
      defaultValue,
      value,
      allowNegative = false,
      locale,
      minimumFractionDigits,
      maximumFractionDigits,
      maximumSignificantDigits,
      trailingZeroDisplay = 'auto',
      signDisplay = 'auto',
      useGrouping = false,
      onChange,
      onBlur,
      ...props
    },
    ref,
  ) => {
    const { stringValue, handleBlur, handleChange } = useNumericInput({
      value,
      defaultValue,
      allowNegative,
      locale,
      minimumFractionDigits,
      maximumFractionDigits,
      maximumSignificantDigits,
      trailingZeroDisplay,
      signDisplay,
      useGrouping,
      onChange,
      onBlur,
    });

    return (
      <TextInput
        {...props}
        onBlur={handleBlur}
        onChange={handleChange}
        ref={ref}
        type="text"
        value={stringValue}
      />
    );
  },
);

NumericInput.displayName = 'NumericInput';

NumericInput.propTypes = {
  ...omitKeys(TextInput.propTypes, [
    'defaultValue',
    'renderIcon',
    'renderLeftIcon',
    'type',
    'value',
    'onChange',
  ]),
  /** The type of input, defaults to 'text' */
  type: PropTypes.oneOf(Object.values(NUMERIC_INPUT_TYPES)),
  /** Default value of the input element */
  defaultValue: PropTypes.number,
  /** Value of the input element */
  value: PropTypes.number,
  /** Whether to allow negative numbers */
  allowNegative: PropTypes.bool,
  /** Locale to use for formatting the number. */
  locale: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  /** Minimum number of fraction digits to use when formatting the number */
  minimumFractionDigits: PropTypes.number,
  /** Maximum number of fraction digits to use when formatting the number. Defaults to 3 or minimumFractionDigits if higher */
  maximumFractionDigits: PropTypes.number,
  /** Maximum number of significant digits to use when formatting the number */
  maximumSignificantDigits: PropTypes.number,
  /** How to display the sign of the number */
  signDisplay: PropTypes.oneOf(Object.values(SIGN_DISPLAY_TYPES)),
  /** How to display trailing zeros in the formatted number */
  trailingZeroDisplay: PropTypes.oneOf(
    Object.values(TRAILING_ZERO_DISPLAY_TYPES),
  ),
  /** Whether to use grouping separators, such as thousands separators or thousand/lakh/crore separators. */
  useGrouping: PropTypes.bool,
  /** Callback fired when the value changes */
  onChange: PropTypes.func,
};

export default Object.assign(NumericInput, {
  TYPES: NUMERIC_INPUT_TYPES,
  SIGN_DISPLAY_TYPES,
  TRAILING_ZERO_DISPLAY_TYPES,
});
